/**
 * localSyntaxErrorSignal — the CONSERVATIVE local tree-sitter parse-error
 * predicate that drives the never-silent compact affordance (VQE Polish,
 * EDG-11..15) for the four engines with NO language-service syntax marker
 * (mysql / oracle / sqlite — and, when the LSP/`sql-syntax` pass is silent,
 * mssql / pgsql too).
 *
 * ── Why this module is honesty-critical ────────────────────────────────────
 * The compact chip asserts "this statement has a syntax error." mysql / oracle
 * / sqlite have NO corroborating second parser (mssql ANDs its tree-sitter
 * against ANTLR precisely to kill false positives — `collectSyntaxMarkers.ts`;
 * we have no such AND-gate for the other three). A tree-sitter `ERROR` node can
 * fire on a LEGAL-but-grammar-unsupported construct (empirically: mssql
 * `SELECT TOP n … ORDER BY`, `FOR JSON PATH`; sqlite `… GLOB …`). Firing the
 * chip / suppressing the pencil on VALID SQL is BOTH a lie AND a BUG-4
 * regression, so this predicate is deliberately biased to UNDER-flag:
 *
 *   `localSyntaxErrorFires` = (a tree-sitter recovery span overlaps the
 *     statement) **AND** (`hasUnambiguousStructuralBreak` on the statement text).
 *
 * The AND is the safety rail — the same "two independent signals must agree"
 * discipline `collectSyntaxMarkers` uses:
 *   • A valid statement tree-sitter happens to reject has NO structural break →
 *     never fires (the false-positive class above is excluded).
 *   • An unbalanced / dangling-operator fragment tree-sitter ACCEPTS as a
 *     recoverable prefix (e.g. `… WHERE x >`, which parses clean) has no
 *     overlapping recovery span → never fires (graceful mid-typing).
 * Only a statement BOTH grammars agree is broken lights the chip.
 *
 * Pure, Monaco-free, no React — unit- and corpus-testable in isolation. The
 * per-engine zero-false-positive corpus (`localSyntaxErrorSignal.corpus.test.ts`)
 * is the load-bearing honesty guard.
 */

import type { SqlRange } from '../../../sql-grammar/model';

/** One tree-sitter recovery span, as `buildErrorRecoveryView` yields it:
 *  `confidence` 1.0 = ERROR range, 0.5 = MISSING range. Both are treated
 *  identically here (each still requires the structural-break gate) — the
 *  maximally conservative choice; empirically MISSING never fires standalone
 *  for these forks, so nothing is lost. */
export type LocalRecoveryEntry = {
  readonly range: SqlRange;
  readonly confidence: number;
};

/** Inclusive-overlap test on absolute UTF-16 offsets — mirrors the diagnostics
 *  suppressor's `endOffset < start || startOffset > end` exclusion (a span that
 *  merely touches an edge still counts, matching the recovery-view semantics). */
export function offsetRangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  if (aEnd < bStart) return false;
  if (aStart > bEnd) return false;
  return true;
}

const QUOTE_CHARS = new Set(["'", '"', '`']);

/** Trailing binary/arithmetic operator or list separator — a statement that
 *  ends here is unfinished. Mirrors `peekResultsDetection.TRAILING_OPERATOR_PATTERN`
 *  (kept in lock-step so "pencil-reachable" and "structurally broken" never
 *  disagree about the same tail). `(` is redundant with the paren-depth check
 *  but harmless. */
const TRAILING_OPERATOR_PATTERN = /(?:<=|>=|<>|!=|::|\|\||[=<>+\-*/%,(])$/;

/** Dangling trailing logical connective (`… AND` / `… OR` / `… NOT`). */
const TRAILING_CONNECTIVE_PATTERN = /(?:^|[^A-Za-z0-9_])(?:AND|OR|NOT)$/i;

type ScanResult = { readonly neutralized: string; readonly unterminatedQuote: boolean };

/**
 * Project `text` to a code-only string: every quoted region (`'…'`, `"…"`,
 * `` `…` ``, doubled delimiter = escape) becomes a run of `x` (same length, so
 * a trailing literal reads as a value — NOT as the operator preceding it), and
 * every comment (line `--` and block) becomes spaces. `unterminatedQuote` is
 * true when a quoted region never closed (an unmistakable structural break).
 * Handling all three quote chars keeps parens inside quoted identifiers /
 * strings from throwing the balance count off on any engine.
 */
function neutralize(text: string): ScanResult {
  let out = '';
  let quote: string | null = null;
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    if (quote) {
      if (c === quote) {
        if (text[i + 1] === quote) {
          out += 'xx';
          i += 2;
          continue;
        }
        quote = null;
        out += 'x';
        i += 1;
        continue;
      }
      out += c === '\n' ? '\n' : 'x';
      i += 1;
      continue;
    }
    if (c === '-' && text[i + 1] === '-') {
      while (i < n && text[i] !== '\n') {
        out += ' ';
        i += 1;
      }
      continue;
    }
    if (c === '/' && text[i + 1] === '*') {
      out += '  ';
      i += 2;
      while (i + 1 < n && !(text[i] === '*' && text[i + 1] === '/')) {
        out += text[i] === '\n' ? '\n' : ' ';
        i += 1;
      }
      if (i + 1 < n) {
        out += '  ';
        i += 2;
      } else {
        // Unterminated block comment — consume the rest as comment.
        while (i < n) {
          out += text[i] === '\n' ? '\n' : ' ';
          i += 1;
        }
      }
      continue;
    }
    if (QUOTE_CHARS.has(c)) {
      quote = c;
      out += 'x';
      i += 1;
      continue;
    }
    out += c;
    i += 1;
  }
  return { neutralized: out, unterminatedQuote: quote !== null };
}

/**
 * True iff `statementText` exhibits an UNAMBIGUOUS structural break — the
 * honesty gate. A valid, complete SQL statement never triggers this (balanced
 * delimiters, closed quotes, no dangling trailing operator), so pairing it with
 * a tree-sitter recovery overlap can never fire on valid SQL. Engine-agnostic:
 * every gate here (unbalanced parens, unterminated quote, trailing operator /
 * connective) is a genuine break in all five dialects.
 */
export function hasUnambiguousStructuralBreak(statementText: string): boolean {
  const trimmed = statementText.replace(/;+\s*$/, '');
  if (!trimmed.trim()) return false;

  const { neutralized, unterminatedQuote } = neutralize(trimmed);
  if (unterminatedQuote) return true;

  let depth = 0;
  for (let i = 0; i < neutralized.length; i += 1) {
    const ch = neutralized[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
  }
  if (depth !== 0) return true;

  const tail = neutralized.replace(/\s+$/, '');
  if (TRAILING_OPERATOR_PATTERN.test(tail)) return true;
  if (TRAILING_CONNECTIVE_PATTERN.test(tail)) return true;

  return false;
}

/**
 * The composed conservative predicate: does the statement spanning
 * `[segStart, segEnd]` (absolute UTF-16 offsets, `segText` = its source slice)
 * genuinely fail to parse? True only when a tree-sitter recovery span overlaps
 * the statement AND the statement text has an unambiguous structural break.
 * Both conditions required — see the module docstring for why the AND is the
 * honesty rail.
 */
export function localSyntaxErrorFires(
  entries: readonly LocalRecoveryEntry[] | null,
  segStart: number,
  segEnd: number,
  segText: string,
): boolean {
  if (!entries || entries.length === 0) return false;
  if (!hasUnambiguousStructuralBreak(segText)) return false;
  return entries.some(
    (e) => e.confidence > 0 && offsetRangesOverlap(e.range.startOffset, e.range.endOffset, segStart, segEnd),
  );
}
